/**
 * `GET /me/permissions` — the accounting session's bootstrap read: the
 * product's state and the companies the person holds Link Accounting access
 * to. The backend serves an ETag with `Cache-Control: no-cache, private`, so
 * the browser's HTTP cache turns an unchanged re-read into a 304.
 */
// eslint-disable-next-line @linkhr/linkhr/no-raw-tanstack-query -- the accounting app keeps its own hooks (it may not import the HR ones); keys come from qk
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { qk, useApiClient, type ApiError, type components } from "@linkhr/api-client/core";
import { useAuth } from "@linkhr/auth/react";

export type MePermissionsView = components["schemas"]["MePermissionsView"];

/** Keyed by the session's tenant, so it can be invalidated from anywhere. */
export const mePermissionsKey = (tenantId: string | null): readonly unknown[] =>
  qk.mePermissions.detail(tenantId ?? "");

// The generated `*/*` response types this GET as `never` (as in the HR hooks):
// read it untyped and type the answer with the generated view.
type AnyGet = { GET: (path: string) => Promise<{ data?: unknown; error?: unknown }> };

export function useMePermissions(): UseQueryResult<MePermissionsView, ApiError> {
  const client = useApiClient() as unknown as AnyGet;
  const { tenantId } = useAuth();
  return useQuery<MePermissionsView, ApiError>({
    queryKey: mePermissionsKey(tenantId),
    queryFn: async () => {
      const res = await client.GET("/api/v1/me/permissions");
      if (res.error !== undefined) throw res.error as ApiError;
      // An empty answer would read as "still loading" forever; make it an error.
      if (res.data === undefined) throw new Error("Empty /me/permissions answer");
      return res.data as MePermissionsView;
    },
  });
}
